import {BaseView} from "@nolimitcity/slot-game/bin/core/base/BaseView";
import {TimelineLite, TweenLite} from "gsap";
import {FITHGameConfig} from "../FITHGameConfig";
import {EventHandler} from "@nolimitcity/slot-game/bin/core/event/EventHandler";
import {ServerEvent} from "@nolimitcity/slot-game/bin/core/server/event/ServerEvent";
import {GameEvent} from "@nolimitcity/slot-game/bin/core/event/GameEvent";
import {SpinEvent} from "@nolimitcity/slot-game/bin/core/spin/event/SpinEvent";
import {IResizeData, StageManager} from "@nolimitcity/slot-game/bin/core/stage/StageManager";
import {FITHParsedGameData} from "../server/data/FITHParsedGameData";
import {FITHGameMode} from "../gameMode/FITHGameMode";
import {FITHGameModuleConfig} from "../FITHGameModuleConfig";
import {GameResources} from "@nolimitcity/slot-game/bin/core/resource/GameResources";
import {Reels} from "@nolimitcity/slot-game/bin/core/reel/reelarea/Reels";
import {ReelController} from "@nolimitcity/slot-game/bin/core/reel/reel/ReelController";
import {GameSetting} from "@nolimitcity/slot-game/bin/core/setting/GameSetting";
import {FITHParsedInitData} from "../server/data/FITHParsedInitData";
import {ArrayHelper} from "@nolimitcity/slot-game/bin/core/utils/ArrayHelper";
import {SpineTween} from "@nolimitcity/slot-game/bin/core/animation/SpineTween";
import {ReelEvent} from "@nolimitcity/slot-game/bin/core/reel/event/ReelEvent";
import {FITHSoundManager} from "../sound/FITHSoundManager";
import {FITHSoundConfig} from "../FITHSoundConfig";

export class FITHBonusGameSpinView extends BaseView {
    private static _reelsFullOriginal: boolean[] = [true, true, true, true, true, true];
    public _trainContainer: PIXI.Container;
    public _wagons: PIXI.spine.Spine[] = [];
    private _wagonHeader: PIXI.Sprite;
    private _trainContainerMask: PIXI.Graphics;
    private _trainInTl: TimelineLite;
    private _trainOutTl: TimelineLite;
    private _trainOutDone: boolean = true;
    private _reels: string[][] = [];
    private _isQuickStop: boolean = false;
    private _allReelsStopped: boolean = false;
    private _mode: string = "";
    private _nextMode: string = "";
    private _bonusWinTimesBetBefore: number;
    private _bonusValuesBeforeSpin: number[][];
    private _bonusValuesAfterSpin: number[][];
    private _dwarfPosition: number[];
    private _reelsFull: boolean[] = [true, true, true, true, true, true];
    private _openPositionsBonus: boolean[][];
    private _chestWagonId: number = -1;
    private _chestAnticipationStopId: number = -1;
    private _dwarfWagonId: number = -1;
    private _dwarfAnticipationStopId: number = -1;
    private _100xWagonId: number[] = [];
    private _100xAnticipationStopId: number[] = [];
    private _x25MultiWagonId: number[] = [];
    private _x25MultiAnticipationStopId: number[] = [];

    public beforeSpinStart(isBonusGameOver: boolean): void {
        // #200
        FITHGameConfig.instance.REEL_SPIN_START_SPEED_BONUS_GAME = 0.2;

        FITHGameConfig.instance.BONUS_GAME_TRAIN_STOPPED = false;
        this._isQuickStop = false;
        this._allReelsStopped = false;
        this._reels = [];

        if (this._nextMode === FITHGameMode.BONUS) {
            // #278
            FITHSoundManager.stopSoundEffect(FITHSoundConfig.instance.FS_REELS_SPINNING);
            FITHSoundManager.stopSoundEffect(FITHSoundConfig.instance.FS_REELS_LANDING);

            this.updateFullReels();
            FITHGameConfig.instance.REEL_STOP_DELAY_BONUS_GAME = ArrayHelper.initArrayWithValues(FITHGameConfig.instance.REELS_NUM, () => 0);

            this.doTrainOut(isBonusGameOver);
            if (!isBonusGameOver) {
                FITHGameModuleConfig.bonusGameSpinNWagonAnimations.dimDwarfSpine();
                FITHGameModuleConfig.bonusGameVSymbolOverlayView.dimAllV();

                // #231
                if (this._wagonHeader.visible) {
                    FITHSoundManager.playFSWagonOut();
                }
            }
        }

        if (isBonusGameOver) {
            this.turnOffTrain();
        }
    }

    public brightWagon(idx: number): void {
        new SpineTween(this._wagons[idx], 0, "bright");
    }

    public dimWagon(idx: number): void {
        new SpineTween(this._wagons[idx], 0, "dark");
    }

    // #298
    private static checkAnticipationStopId(reelId: number): number {
        reelId *= 6;
        for (let i: number = (reelId + 4); i > reelId; i--) {
            if (FITHGameModuleConfig.spinController.getSpinStatusOfReel(i)) {
                return i;
            }
        }
        return reelId;
    }

    protected addEventListeners(): void {
        super.addEventListeners && super.addEventListeners();

        EventHandler.addEventListener(this, ServerEvent.INIT_DATA_PARSED, (event: GameEvent) => this.onInitDataParsed(event.params[0]));
        EventHandler.addEventListener(this, ServerEvent.GAME_DATA_PARSED, (event: GameEvent) => this.onGameDataParsed(event.params[0]));
        EventHandler.addEventListener(this, SpinEvent.STOP, (event: GameEvent) => this.onStopSpin(event.params[0]));
        EventHandler.addEventListener(this, ReelEvent.ALL_REELS_SPIN_STOPPED, () => this._allReelsStopped = true);
    }

    protected initAnimations(): void {
        super.initAnimations && super.initAnimations();
        StageManager.getLayer(FITHGameConfig.instance.LAYERS.BONUS_OVERLAY.name).addChild(this);

        this._trainContainerMask = new PIXI.Graphics();
        this.mask = this._trainContainerMask;
        this.addChild(this._trainContainerMask);

        this.createTrainContainer();
    }

    protected onResize(resizeData: IResizeData): void {
        super.onResize && super.onResize(resizeData);

        // to update the mask only if in bonus mode.
        if (this._mode === FITHGameMode.BONUS || this._nextMode === FITHGameMode.BONUS) {
            this.updateTrainContainerMask();
        }
    }

    protected turnOffTrain(): void {
        let wagon: PIXI.spine.Spine;
        for (let i: number = 0; i < this._wagons.length; i++) {
            wagon = this._wagons[i];
            wagon.visible = false;
            wagon.state.clearTracks();
        }
        this._wagonHeader.visible = false;
    }

    // #298
    public reelStoppedInBonus(id: number): void {
        if (id === this._chestAnticipationStopId) {
            FITHGameModuleConfig.anticipationView.stopWagonAnticipationCurrentReel(Math.floor((id / 6)));

            // #312
            FITHSoundManager.stopSoundEffect(FITHSoundConfig.instance.FS_CHEST_ANTICIPATION);
        } else if (id === this._dwarfAnticipationStopId) {
            FITHGameModuleConfig.anticipationView.stopWagonAnticipationCurrentReel(Math.floor((id / 6)));

            // #312
            FITHSoundManager.stopSoundEffect(FITHSoundConfig.instance.FS_DWARF_ANTICIPATION);
        } else if (this._100xAnticipationStopId.includes(id)) {
            FITHGameModuleConfig.anticipationView.stopWagonAnticipationCurrentReel(Math.floor((id / 6)));

            // #312
            FITHSoundManager.stopSoundEffect(FITHSoundConfig.instance.FS_DWARF_ANTICIPATION);
        } else if (this._x25MultiAnticipationStopId.includes(id)) {
            FITHGameModuleConfig.anticipationView.stopWagonAnticipationCurrentReel(Math.floor((id / 6)));

            // #312
            FITHSoundManager.stopSoundEffect(FITHSoundConfig.instance.FS_DWARF_ANTICIPATION);
        }
    }

    private onInitDataParsed(data: FITHParsedInitData): void {
        this._nextMode = data.nextMode;
        this._bonusValuesAfterSpin = data.bonusValues;
        this._openPositionsBonus = data.openPositionsBonus;

        EventHandler.removeEventListener(this, ServerEvent.INIT_DATA_PARSED);
    }

    private onGameDataParsed(data: FITHParsedGameData): void {
        this._mode = data.mode;
        this._nextMode = data.nextMode;
        this._bonusValuesAfterSpin = data.bonusValuesAfterSpin;
        this._openPositionsBonus = data.openPositionsBonus;
        this._dwarfPosition = data.dwarfPosition;

        if (data.mode === FITHGameMode.BONUS) {
            this._reels = data.reels;
            this._bonusWinTimesBetBefore = data.bonusWinTimesBetBefore;

            this.tryTrainIn();
        }
    }

    private tryTrainIn(): void {
        if (this._trainOutDone && this._reels.length > 0) {
            this.updateTrain();
            this.updateTrainContainerMask();
            this.doTrainIn();
        }
    }

    private updateTrain(): void {
        let wagon: PIXI.spine.Spine,
            wagonName: string;

        const firstWagonX: number = 190,
            xPosOffset: number = 115,
            reelsNumOriginal: number = FITHGameConfig.instance.REELS_NUM_ORIGINAL;

        this._wagonHeader.visible = true;

        // #298
        this._chestWagonId = this._dwarfWagonId = -1;
        this._chestAnticipationStopId = this._dwarfAnticipationStopId = -1;
        this._100xWagonId = [];
        this._x25MultiWagonId = [];
        this._100xAnticipationStopId = [];
        this._x25MultiAnticipationStopId = [];

        // updating wagons
        for (let i: number = 0; i < reelsNumOriginal; i++) {
            wagonName = this._reels[i * reelsNumOriginal][0]; // it is always the first element in each reel, hence [0].
            wagon = this._wagons[i];

            wagon.skeleton.setSkin(null);
            wagon.skeleton.setSkinByName(wagonName);
            wagon.skeleton.setSlotsToSetupPose();
            wagon.state.clearTracks();
            new SpineTween(wagon, 0, "in");
            wagon.updateTransform();
            wagon.visible = true;
            wagon.x = ((firstWagonX + (i * xPosOffset)) + (wagon.width / 2));
            wagon.y = (wagon.height / 2);

            // #150
            if (wagonName === "DW") {
                wagon.x -= 15;
                wagon.y -= 25;

                // #298
                this._dwarfWagonId = i;
                this._dwarfAnticipationStopId = FITHBonusGameSpinView.checkAnticipationStopId(i);
            }

            // #298
            if (wagonName === "G") {
                this._chestWagonId = i;
                this._chestAnticipationStopId = FITHBonusGameSpinView.checkAnticipationStopId(i);
            }
            if (wagonName === "C100") {
                this._100xWagonId.push(i);
                this._100xAnticipationStopId.push(FITHBonusGameSpinView.checkAnticipationStopId(i));
            }
            if (wagonName === "X25") {
                this.checkForX25Anticipation(i);
            }
        }

        this._trainContainer.x = FITHGameConfig.instance.REEL_AREA_WIDTH - firstWagonX;
    }

    // #298
    private checkForX25Anticipation(id: number): void {
        const beforeValues: number[] = this._bonusValuesBeforeSpin[id],
            dwarfPosition: number[] = this._dwarfPosition;
        let totalBeforeValue: number = 0,
            totalAfterValue: number = 0;

        for (let i: number = 1; i < this._bonusValuesBeforeSpin[id].length - 1; i++) {
            if (id !== dwarfPosition[0] || i !== dwarfPosition[1]) {
                totalBeforeValue += beforeValues[i];

                totalAfterValue += (beforeValues[i] * 25);
            }
        }

        if (((totalAfterValue + 25) - totalBeforeValue) > 99) {
            this._x25MultiWagonId.push(id);
            this._x25MultiAnticipationStopId.push(FITHBonusGameSpinView.checkAnticipationStopId(id));
        }
    }

    private doTrainIn(): void {
        this._trainInTl = new TimelineLite({
            onComplete: () => {
                let wagon: PIXI.spine.Spine,
                    animName: string;

                FITHGameConfig.instance.BONUS_GAME_TRAIN_STOPPED = true;

                // #298
                this.checkForWagonAnticipation();

                for (let i: number = 0; i < this._wagons.length; i++) {
                    wagon = this._wagons[i];
                    if (this._reelsFull[i]) {
                        animName = "dark";
                    } else {
                        animName = "base";
                    }
                    new SpineTween(wagon, 0, animName);
                }
                this._reelsFull = [...FITHBonusGameSpinView._reelsFullOriginal]; // reset it.

                this.doSpinning();
            }
        });

        // #231
        FITHSoundManager.playFSWagonIn();

        // #291
        if (this.checkForSpecialWagonSound("DW")) {
            FITHSoundManager.playFSDwarfEnters();
        }
        // #292
        if (this.checkForSpecialWagonSound("G")) {
            FITHSoundManager.playFSChestEnters();
        }
        // #307
        if (this.checkForSpecialWagonSound("X25")) {
            FITHSoundManager.playFS25MultiEnters();
        }
        // #307
        if (this.checkForSpecialWagonSound("C100")) {
            FITHSoundManager.playFS100xEnters();
        }

        this._trainInTl.to(this._trainContainer, ((GameSetting.isFastSpin || this._isQuickStop) ? 0.3 : 0.7), {x: (this._trainContainer.x - FITHGameConfig.instance.REEL_AREA_WIDTH)});
    }

    private checkForSpecialWagonSound(wagonToCheck: string): boolean {
        const reelsNumOriginal: number = FITHGameConfig.instance.REELS_NUM_ORIGINAL;
        for (let i: number = 0; i < reelsNumOriginal; i++) {
            if (this._reels[i * reelsNumOriginal][0] === wagonToCheck) {
                return true;
            }
        }

        return false;
    }

    // #298
    private checkForWagonAnticipation(): void {
        if (this._chestWagonId !== -1 && !this._reelsFull[this._chestWagonId]) {
            FITHGameModuleConfig.anticipationView.playWagonAnticipationCurrentReel(this._chestWagonId);

            // #312
            FITHSoundManager.playFSChestAnticipation();
        }
        if (this._dwarfWagonId !== -1 && !this._reelsFull[this._dwarfWagonId]) {
            FITHGameModuleConfig.anticipationView.playWagonAnticipationCurrentReel(this._dwarfWagonId);

            // #312
            FITHSoundManager.playFSDwarfAnticipation();
        }
        for (let i: number = 0; i < this._100xWagonId.length; i++) {
            if (!this._reelsFull[this._100xWagonId[i]]) {
                FITHGameModuleConfig.anticipationView.playWagonAnticipationCurrentReel(this._100xWagonId[i]);

                // #312
                if (i === 0) {
                    FITHSoundManager.playFSDwarfAnticipation();
                }
            }
        }
        for (let i: number = 0; i < this._x25MultiWagonId.length; i++) {
            if (!this._reelsFull[this._x25MultiWagonId[i]]) {
                FITHGameModuleConfig.anticipationView.playWagonAnticipationCurrentReel(this._x25MultiWagonId[i]);

                // #312
                if (i === 0) {
                    FITHSoundManager.playFSDwarfAnticipation();
                }
            }
        }
    }

    private doTrainOut(isBonusGameOver: boolean): void {
        this._trainOutTl = new TimelineLite({
            onComplete: () => {
                this.turnOffTrain();
                this._trainOutDone = true;
                if (!isBonusGameOver) {
                    this.tryTrainIn();
                }
            }
        });

        this._trainOutDone = false;
        this._trainOutTl.to(this._trainContainer, isBonusGameOver ? 0.01 : (GameSetting.isFastSpin ? 0.3 : 0.7), {x: (this._trainContainer.x - FITHGameConfig.instance.REEL_AREA_WIDTH)});
    }

    private createTrainContainer(): void {
        this._trainContainer = new PIXI.Container();
        this._trainContainer.y = 105;

        this.createTrain();
        this.addChild(this._trainContainer);
    }

    private createTrain(): void {
        let wagon: PIXI.spine.Spine;

        const reelsNumOriginal: number = FITHGameConfig.instance.REELS_NUM_ORIGINAL;

        // adding header wagon
        this._wagonHeader = new PIXI.Sprite(GameResources.getTextures("Wagon_header")[0]);
        this._wagonHeader.visible = false;
        this._trainContainer.addChild(this._wagonHeader);

        // adding remaining wagons
        for (let i: number = 0; i < reelsNumOriginal; i++) {
            wagon = new PIXI.spine.Spine(GameResources.getSpineAsset("wagon"));
            wagon.visible = false;
            wagon.skeleton.setSkin(null);
            wagon.state.clearTracks();

            this._trainContainer.addChild(wagon);
            this._wagons.push(wagon);
        }
    }

    private updateTrainContainerMask(): void {
        this._trainContainerMask.clear();
        this._trainContainerMask.beginFill(0xffffff, 1);
        this._trainContainerMask.drawRect(10, 0, 700, 250);
        this._trainContainerMask.endFill();
        if (this._trainContainerMask !== null) {
            this.removeChild(this._trainContainerMask);
        }
        this.mask = this._trainContainerMask;
        this.addChild(this._trainContainerMask);
    }

    private onStopSpin(isQuickStop: boolean): void {
        // is QuickStop is always true at the moment from the framework, just kept it, in case it is changed from framework/future use.
        this._isQuickStop = isQuickStop;
        if (isQuickStop && this._mode === FITHGameMode.BONUS) {
            // #200
            FITHGameConfig.instance.REEL_SPIN_START_SPEED_BONUS_GAME = 0.001;

            // so that train-out is stopped immediately.
            if (this._trainOutTl && this._trainOutTl.totalProgress() > 0 && this._trainOutTl.totalProgress() < 1) {
                this._trainOutTl.totalProgress(1);
            }
            // so that train-in is stopped immediately and so do the reels.
            else if (this._trainInTl && this._trainInTl.totalProgress() > 0 && this._trainInTl.totalProgress() < 1) {
                this._trainInTl.totalProgress(1);
            }
            // #122 if all reels haven't stopped and we get a quick stop action(spinning is still visible on reels but train has stopped),
            // then we re-run the whole reels stop, it didn't have side effect when tested during implementation.
            else if (!this._allReelsStopped) {
                this.doStopping();
            }
        }
    }

    private doSpinning(): void {
        const reelsNum: number = FITHGameConfig.instance.REELS_NUM,
            reelsNumOriginal: number = FITHGameConfig.instance.REELS_NUM_ORIGINAL;

        let active: boolean,
            reel: ReelController | null,
            wagonName: string,
            delay: number = 0;

        // #231
        FITHSoundManager.playFSReelsSpinning();
        // starting all reels.
        for (let i: number = 0; i < reelsNumOriginal; i++) {
            for (let j: number = i; j < reelsNum; j += 6) {
                active = FITHGameModuleConfig.spinController.getSpinStatusOfReel(j);
                reel = Reels.getReel(j);
                if (reel) {
                    delay = reel.startSpin({
                        active: active,
                        fastSpin: GameSetting.isFastSpin
                    }, this._isQuickStop ? 0 : delay);
                }
            }
        }

        // #200
        if (this._isQuickStop) {
            TweenLite.delayedCall(0.07, () => this.doStopping());
            return; // no need to set stop delays.
        } else if (GameSetting.isFastSpin) {
            TweenLite.delayedCall(delay + 0.25, () => this.doStopping());
            return; // no need to set stop delays.
        }
        
        // setting stop delays.
        for (let i: number = 0; i < reelsNum; i += FITHGameConfig.instance.REELS_NUM_ORIGINAL) {
            wagonName = this._reels[i][0];
            FITHBonusGameSpinView.setStopDelays(wagonName, i);
        }
        TweenLite.delayedCall(delay + 0.25, () => this.doStopping());
    }

    private static setStopDelays(name: string, firstReelId: number): void {
        let active: boolean;

        for (let i: number = firstReelId, j = 0; i < (firstReelId + FITHGameConfig.instance.REELS_NUM_ORIGINAL); i++) {
            active = FITHGameModuleConfig.spinController.getSpinStatusOfReel(i);

            if (active) {
                FITHGameConfig.instance.REEL_STOP_DELAY_BONUS_GAME[i] = 0.07;
                j++;
            }
        }
    }

    private doStopping(): void {
        const reelsNum: number = FITHGameConfig.instance.REELS_NUM,
            reelsNumOriginal: number = FITHGameConfig.instance.REELS_NUM_ORIGINAL;

        let delay: number = 0.01,
            active: boolean,
            reel: ReelController | null;

        // stopping all reels.
        for (let i: number = 0; i < reelsNumOriginal; i++) {
            for (let j: number = i; j < (reelsNum); j += 6) {
                active = FITHGameModuleConfig.spinController.getSpinStatusOfReel(j);
                reel = Reels.getReel(j);
                if (reel) {
                    delay = reel.stopSpin({
                        active: active,
                        quickStop: this._isQuickStop,
                        fastSpin: GameSetting.isFastSpin,
                        spinStopOffset: 0
                    }, delay);
                }
            }
        }
    }

    private updateFullReels(): void {
        let reelPositions: boolean[];

        this._bonusValuesBeforeSpin = [...this._bonusValuesAfterSpin];

        for (let i: number = 0; i < this._openPositionsBonus.length; i++) {
            reelPositions = this._openPositionsBonus[i];

            for (let j: number = 1; j < (reelPositions.length - 1); j++) {
                if (!reelPositions[j]) {
                    break;
                }

                if (this._bonusValuesAfterSpin[i][j] === 0) {
                    this._reelsFull[i] = false;
                    break;
                }
            }
        }
    }
}